import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { DomainService } from './domain.service';
import { CookieService } from './cookie.service';
import { User, AuthResponse, UserRoleType } from '../../shared/interfaces/auth.interfaces';

const DOCS_ORIGIN_STORAGE_KEY = 'docs_origin';
const DEFAULT_DOCS_ORIGIN = 'https://docs.mataloka.com';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  private tokenSubject = new BehaviorSubject<string | null>(null);

  public currentUser$ = this.currentUserSubject.asObservable();
  public token$ = this.tokenSubject.asObservable();

  private http = inject(HttpClient);
  private domainService = inject(DomainService);
  private cookieService = inject(CookieService);

  constructor() {
    this.loadStoredAuth();
    this.checkUrlAuth();
  }

  private loadStoredAuth(): void {
    // Try cookies first (for cross-subdomain support)
    let token = this.cookieService.getCookie('auth_token');
    let userStr = this.cookieService.getCookie('current_user');

    // Fallback to localStorage if cookies not found
    if (!token || !userStr) {
      token = localStorage.getItem('auth_token');
      userStr = localStorage.getItem('current_user');

      // If found in localStorage, migrate to cookies
      if (token && userStr) {
        this.cookieService.setCookie('auth_token', token, 7);
        this.cookieService.setCookie('current_user', userStr, 7);

        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          this.cookieService.setCookie('refresh_token', refreshToken, 7);
        }
      }
    }

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);

        this.tokenSubject.next(token);
        this.currentUserSubject.next(user);
      } catch (error) {
        console.error('❌ [AuthService] Failed to parse stored auth data:', error);
        this.clearAuth();
      }
    } else {
      console.log('ℹ️ [AuthService] No stored auth data found');
    }
  }

  private checkUrlAuth(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const userParam = urlParams.get('user');
    const refreshToken = urlParams.get('refreshToken');
    const isLogout = urlParams.get('logout');
    const logoutReason = urlParams.get('reason');

    if (isLogout === 'true') {
      this.clearAuth();
      if (logoutReason) {
        try {
          sessionStorage.setItem('logout_reason', logoutReason);
        } catch {
        }
      }
      this.cleanLogoutParams();
      return;
    }

    if (token && userParam && refreshToken) {
      try {
        const decodedToken = decodeURIComponent(token);
        const decodedUser = JSON.parse(decodeURIComponent(userParam));
        const decodedRefreshToken = decodeURIComponent(refreshToken);

        const authResponse = {
          token: decodedToken,
          user: decodedUser,
          refreshToken: decodedRefreshToken
        };

        // CROSS-DOMAIN FIX: Set auth data dengan domain wildcard untuk sharing
        this.setAuth(authResponse);

        // CROSS-DOMAIN FIX: Clean URL parameters tanpa reload untuk mencegah loop
        this.cleanUrlParams();

        // CROSS-DOMAIN FIX: Trigger navigation ke route yang sesuai tanpa reload
        const currentDomain = this.domainService.getCurrentDomain();
        const userType = this.getUserType();

        if (currentDomain !== 'localhost' && userType) {
          // Cek apakah user sudah berada di domain yang benar
          const isInCorrectDomain = this.domainService.isUserInCorrectDomain(userType);

          if (!isInCorrectDomain) {
            // Redirect ke domain yang benar
            const correctUrl = this.domainService.getRedirectUrl(userType);
            window.location.href = correctUrl;
          } else {
            // Sudah di domain yang benar, navigasi ke default route
            console.log('✅ [AuthService] Already in correct domain, navigating to default route');
            // Tidak perlu reload, biarkan Angular router handle navigation
          }
        }

      } catch (error) {
        console.error('❌ [AuthService] Error processing URL auth parameters:', error);
        this.clearAuth();
      }
    }
  }

  private cleanUrlParams(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('token');
    url.searchParams.delete('user');
    url.searchParams.delete('refreshToken');

    window.history.replaceState({}, document.title, url.toString());
  }

  private cleanLogoutParams(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('logout');
    url.searchParams.delete('reason');

    window.history.replaceState({}, document.title, url.toString());
  }

  setAuth(authResponse: AuthResponse): void {

    if (!authResponse || !authResponse.user) {
      this.clearAuth();
      return;
    }

    const memberTypeName = authResponse.user?.jns_anggota?.nama;

    let accessLevel: 'admin' | 'user' | 'in-house' | null;
    if (memberTypeName === 'Media Owners') {
      accessLevel = 'user';
    } else if (memberTypeName === 'Operator') {
      accessLevel = 'in-house';
    } else {
      accessLevel = 'user';
    }

    const mappedUser: User = {
      id: authResponse.user?.idu || '',
      email: authResponse.user?.email || '',
      name: authResponse.user?.nama_lkp || '',
      userType: memberTypeName as UserRoleType,
      accessLevel: accessLevel,
      roles: [], // Anda mungkin perlu mengisi ini berdasarkan data yang tersedia
      jns_anggota: memberTypeName
    };

    // CROSS-DOMAIN FIX: Store in cookies dengan wildcard domain untuk cross-subdomain access
    // Use dynamic domain from CookieService
    let cookieDomain: string | undefined = undefined;
    const dynamicDomain = this.cookieService.getCurrentSpecificDomain();

    if (dynamicDomain) {
      cookieDomain = dynamicDomain;
    }
    // else: biarkan undefined agar menggunakan Host-Only cookie (default browser)

    this.cookieService.setCookie('auth_token', authResponse.token, 7, cookieDomain);
    this.cookieService.setCookie('current_user', JSON.stringify(mappedUser), 7, cookieDomain);
    this.cookieService.setCookie('refresh_token', authResponse.refreshToken, 7, cookieDomain);

    // Also store in localStorage as backup
    localStorage.setItem('auth_token', authResponse.token);
    localStorage.setItem('current_user', JSON.stringify(mappedUser));
    localStorage.setItem('refresh_token', authResponse.refreshToken);

    this.tokenSubject.next(authResponse.token);
    this.currentUserSubject.next(mappedUser);
  }

  clearAuth(): void {
    // CROSS-DOMAIN FIX: Clear cookies dengan domain yang sama seperti saat set
    let cookieDomain: string | undefined = undefined;
    const dynamicDomain = this.cookieService.getCurrentSpecificDomain();

    if (dynamicDomain) {
      cookieDomain = dynamicDomain;
    }

    this.cookieService.deleteCookie('auth_token', cookieDomain);
    this.cookieService.deleteCookie('current_user', cookieDomain);
    this.cookieService.deleteCookie('refresh_token', cookieDomain);

    // Clear localStorage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    localStorage.removeItem('refresh_token');

    this.tokenSubject.next(null);
    this.currentUserSubject.next(null);
  }

  logout(reason?: string): void {
    this.clearAuth();
    const config = this.domainService.getConfig();
    const loginUrl = config.loginDomain || this.domainService.getLoginUrl();

    const logoutUrl = new URL(loginUrl);
    logoutUrl.searchParams.set('logout', 'true');
    if (reason) {
      logoutUrl.searchParams.set('reason', reason);
    }

    const finalRedirect = () => { window.location.href = logoutUrl.toString(); };

    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const docsOrigin = isLocalDev ? 'http://localhost:4317' : this.getStoredDocsOrigin();
    const docsClearUrl = `${docsOrigin}/clear-token.html`;

    let redirected = false;
    const safeRedirect = () => {
      if (!redirected) {
        redirected = true;
        finalRedirect();
      }
    };

    const fallbackTimer = setTimeout(safeRedirect, 3000);

    const allowedDocsOrigins = ['http://localhost:4317', DEFAULT_DOCS_ORIGIN, docsOrigin];
    const onMessage = (event: MessageEvent) => {
      if (allowedDocsOrigins.includes(event.origin) && event.data?.type === 'DOCS_TOKEN_CLEARED') {
        window.removeEventListener('message', onMessage);
        clearTimeout(fallbackTimer);
        safeRedirect();
      }
    };
    window.addEventListener('message', onMessage);

    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'display:none;width:0;height:0;border:none;position:absolute;';
    iframe.src = docsClearUrl;
    document.body.appendChild(iframe);

    return;
  }

  private getStoredDocsOrigin(): string {
    try {
      const stored = localStorage.getItem(DOCS_ORIGIN_STORAGE_KEY);
      if (stored) {
        return new URL(stored).origin;
      }
    } catch (error) {
      console.warn('Failed to read stored docs origin:', error);
    }

    return DEFAULT_DOCS_ORIGIN;
  }

  isAuthenticated(): boolean {
    return this.tokenSubject.value !== null;
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  getUserType(): UserRoleType | null {
    const user = this.getCurrentUser();
    return user ? user.userType : null;
  }

  getAccessLevel(): 'admin' | 'user' | 'in-house' | null {
    const user = this.getCurrentUser();
    return user ? user.accessLevel : null;
  }

  getToken(): string | null {
    return this.tokenSubject.value;
  }

  refreshToken(): Observable<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    const config = this.domainService.getConfig();

    return this.http.post<AuthResponse>(`${config.apiUrl}/auth/refresh`, {
      refreshToken
    });
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user ? user.roles.includes(role) : false;
  }

  canAccessDSP(): boolean {
    const userType = this.getUserType();
    const accessLevel = this.getAccessLevel();
    return userType === 'Advertisers' || userType === 'Operator' || accessLevel === 'in-house';
  }

  canAccessSSP(): boolean {
    const userType = this.getUserType();
    const accessLevel = this.getAccessLevel();
    return userType === 'Media Owners' || userType === 'Operator' || accessLevel === 'in-house';
  }
}
